import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useSetRecoilState } from 'recoil';
import { toastStateSelector } from 'state';
import { useFormik } from 'formik';
import { ToastSeverity } from 'state';

import { Paper, TextField, Grid } from '@mui/material';

import { useTranslation } from 'react-i18next';

import { GeneralIds, ModelTypes, RoutePaths } from 'types/types';
import { Box } from '@mui/system';
import {
  getUserSignUpInitialValues,
  getUserSignUpValidationSchema,
} from 'forms';

import { mutation } from 'operations/mutation';
import MuiPhoneNumber from 'mui-phone-number';
import { SaveButton } from 'components/SaveButton';

export default function UserSignUp() {
  const { id: userId, projectId } = useParams<GeneralIds>();
  const { t, i18n } = useTranslation();
  const setToast = useSetRecoilState(toastStateSelector);

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const formik = useFormik({
    initialValues: getUserSignUpInitialValues(),
    enableReinitialize: true,
    validationSchema: getUserSignUpValidationSchema(t),
    onSubmit: async values => {
      const { address, firstName, lastName, phone, password } = values;

      try {
        await mutation({
          type: ModelTypes.userSignUp,
          body: {
            address,
            firstName,
            lastName,
            phone,
            id: userId ?? '',
            // in case of userSignUp we cant query the user so we send id as userId, normaly userId is the email
            userId: userId ?? '',
            password,
            token: searchParams.get('token'),
          },
          ids: { userId, projectId: projectId },
        });
        setToast({
          isOpen: true,
          message: t('general.success.save'),
          severity: ToastSeverity.success,
        });
        navigate(RoutePaths.login);
      } catch (e: any) {
        const errorCode = e.response?.data?.error?.[0].code;
        setToast({
          isOpen: true,
          message: i18n.exists(`errors.${errorCode}`)
            ? t(`errors.${errorCode}`)
            : t('general.errors.save'),
          severity: ToastSeverity.error,
        });
      }
    },
  });

  return (
    <Paper
      sx={{
        padding: theme => theme.spacing(3),
        mt: theme => theme.spacing(3),
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <h2>{t('general.registration')}</h2>
      </Box>
      <form onSubmit={formik.handleSubmit}>
        <Grid container spacing={2} mt={5}>
          <Grid xs={12} md={6} item>
            <TextField
              fullWidth
              margin="normal"
              label={`${t('general.lastName')} *`}
              type="text"
              name="lastName"
              value={formik.values.lastName}
              onChange={formik.handleChange}
              error={formik.touched.lastName && Boolean(formik.errors.lastName)}
              helperText={formik.touched.lastName && formik.errors.lastName}
            />
          </Grid>
          <Grid xs={12} md={6} item>
            <TextField
              fullWidth
              margin="normal"
              label={`${t('general.firstName')} *`}
              type="text"
              name="firstName"
              value={formik.values.firstName}
              onChange={formik.handleChange}
              error={
                formik.touched.firstName && Boolean(formik.errors.firstName)
              }
              helperText={formik.touched.firstName && formik.errors.firstName}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid xs={12} md={12} item>
            <TextField
              fullWidth
              margin="normal"
              label={`${t('login.password')} *`}
              type="password"
              name="password"
              value={formik.values.password}
              onChange={formik.handleChange}
              error={formik.touched.password && Boolean(formik.errors.password)}
              helperText={formik.touched.password && formik.errors.password}
            />
          </Grid>
          <Grid xs={12} md={12} item>
            <TextField
              fullWidth
              margin="normal"
              label={`${t('login.confirmPassword')} *`}
              type="password"
              name="confirmPassword"
              value={formik.values.confirmPassword}
              onChange={formik.handleChange}
              error={
                formik.touched.confirmPassword &&
                Boolean(formik.errors.confirmPassword)
              }
              helperText={
                formik.touched.confirmPassword && formik.errors.confirmPassword
              }
            />
          </Grid>
          <Grid xs={12} md={6} item>
            <MuiPhoneNumber
              variant="outlined"
              fullWidth
              margin="normal"
              value={formik.values.phone}
              label={`${t('general.phone')} *`}
              defaultCountry={'hu'}
              onChange={e => {
                // @ts-ignore
                formik.setFieldValue('phone', e?.replaceAll(' ', ''));
              }}
              name="phone"
              regions={'europe'}
              error={formik.touched.phone && Boolean(formik.errors.phone)}
              helperText={formik.touched.phone && formik.errors.phone}
            />
          </Grid>
          <Grid xs={12} md={6} item>
            <TextField
              fullWidth
              margin="normal"
              label={t('general.address')}
              type="text"
              name="address"
              value={formik.values.address}
              onChange={formik.handleChange}
              error={formik.touched.address && Boolean(formik.errors.address)}
              helperText={formik.touched.address && formik.errors.address}
            />
          </Grid>

          <Box display="flex" justifyContent="end">
            <SaveButton isLoading={formik.isSubmitting} />
          </Box>
        </Grid>
      </form>
    </Paper>
  );
}
